import React,{useState,useEffect}from"react";
import{X,GitBranch,Repeat,ChevronDown}from"lucide-react";
import{BLUE,newId,getAssignableUsers}from"../config";
import{projectDisplayName,getProjects}from"../Graph";
import{Inp,Txa,Sel,Spin,VisIcon,StaBadge}from"./UI";
import DepsPicker from"./DepsPicker";
import AssigneePicker,{AssigneeChip}from"./AssigneePicker";

const DAYS=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const FREQ_OPTS=[
  {v:"daily",lb:"Every day"},
  {v:"weekdays",lb:"Every weekday (Mon–Fri)"},
  {v:"weekly",lb:"Weekly"},
  {v:"biweekly",lb:"Every 2 weeks"},
  {v:"monthly",lb:"Monthly (day of month)"},
  {v:"custom",lb:"Custom — every N days"},
];

export default function TaskModal({onClose,onAdd,all,cu,projects=[],allUsers=[]}){
  const[form,setForm]=useState({
    ti:"",de:"",pr:"medium",st:"todo",du:"",vi:"personal",as:"",dp:[],tg:"",no:"",proj:"",
    recur:false,recurFreq:"weekly",recurDays:[],recurInterval:7,recurDay:1
  });
  const[localProjects,setLocalProjects]=useState(projects);
  const[projLoading,setProjLoading]=useState(false);
  const[projError,setProjError]=useState("");
  const[showDeps,setShowDeps]=useState(false);
  const[showAssignee,setShowAssignee]=useState(false);
  const fetchProjects=()=>{
    setProjLoading(true);setProjError("");
    getProjects().then(p=>{setLocalProjects(p);if(p.length===0)setProjError("No active projects found. Ask an Admin to add projects.");})
      .catch(e=>{setProjError("Could not load projects: "+e.message);})
      .finally(()=>setProjLoading(false));
  };
  useEffect(()=>{
    if(projects.length>0){setLocalProjects(projects);return;}
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[projects]);
  const[step,setStep]=useState(1);
  const[saving,setSaving]=useState(false);
  const upd=(k,v)=>setForm(p=>({...p,[k]:v}));
  const togDay=d=>upd("recurDays",form.recurDays.includes(d)?form.recurDays.filter(x=>x!==d):[...form.recurDays,d]);
  const activeProjects=localProjects.filter(p=>p.isActive);
  const customerProjects=activeProjects.filter(p=>p.projectType==="customer").sort((a,b)=>{
    const cmp=(a.customerName||"").localeCompare(b.customerName||"");
    if(cmp!==0)return cmp;
    return(a.jobNumber||"").localeCompare(b.jobNumber||"",undefined,{numeric:true});
  });
  const internalProjects=activeProjects.filter(p=>p.projectType==="internal").sort((a,b)=>(a.projectName||"").localeCompare(b.projectName||""));
  const assignable=getAssignableUsers(cu,allUsers);
  const valid=form.ti.trim().length>0&&(form.vi!=="company"||form.proj!=="")&&(form.vi!=="assigned"||(form.as!==""&&assignable.length>0));

  const depTasks=(form.dp||[]).map(id=>all.find(t=>t.id===id)).filter(Boolean);

  const submit=async()=>{
    if(!valid)return;setSaving(true);
    const asgn=(form.vi==="personal"?cu.email:form.as)||"";
    const isPeer=asgn&&asgn.toLowerCase()!==cu.email.toLowerCase();
    const recurrence=form.recur?{enabled:true,freq:form.recurFreq,days:form.recurDays,interval:form.recurInterval,dayOfMonth:form.recurDay}:null;
    await onAdd({id:newId(),spId:null,own:cu.email,asgn,vis:form.vi,title:form.ti.trim(),desc:form.de,pri:form.pr,sta:isPeer?"pending":form.st,dueDate:form.du||"",tags:form.tg?form.tg.split(",").map(x=>x.trim()).filter(Boolean):[],reminderDate:"",reminderOutlook:false,reminderTeams:false,reminderEmail:false,docs:[],notes:form.no,blockReason:"",deps:form.dp,reqBy:isPeer?cu.email:"",acc:isPeer?"pending":"",src:"manual",standup:false,standupHistory:[],project:form.proj,recurrence});
    onClose();
  };

  const handleBackdrop=e=>{
    if(e.target!==e.currentTarget)return;
    const dirty=form.ti.trim()||form.de.trim()||form.dp.length>0||form.recur||form.tg.trim()||form.no.trim();
    if(dirty&&!window.confirm("Discard this task?"))return;
    onClose();
  };

  const steps=["Basics","Details"];
  return(
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.45)"}} onClick={handleBackdrop}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl flex flex-col" style={{maxHeight:"88vh"}}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <p className="font-bold">New Task <span className="text-xs text-slate-400 font-normal">as {cu.name}</span></p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={17}/></button>
        </div>
        <div className="flex border-b border-slate-200 shrink-0">
          {steps.map((s,i)=><button key={s} onClick={()=>setStep(i+1)} className="flex-1 py-2.5 text-xs font-semibold border-b-2" style={step===i+1?{borderColor:BLUE,color:BLUE,background:BLUE+"11"}:{borderColor:"transparent",color:"#94A3B8"}}>{i+1}. {s}</button>)}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {step===1&&<>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Title *</label><Inp value={form.ti} onChange={e=>upd("ti",e.target.value)} placeholder="What needs to be done?"/></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Description</label><Txa value={form.de} onChange={e=>upd("de",e.target.value)} rows={2} placeholder="More context..."/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Priority</label><Sel value={form.pr} onChange={e=>upd("pr",e.target.value)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></Sel></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Due Date</label><Inp type="date" value={form.du} onChange={e=>upd("du",e.target.value)}/></div>
            </div>

            {/* Recurring */}
            <div className="space-y-3">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={form.recur} onChange={e=>upd("recur",e.target.checked)} className="accent-blue-600 w-4 h-4"/>
                <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Repeat size={14} className="text-slate-400"/>Recurring task</span>
              </label>
              {form.recur&&(
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Frequency</label>
                    <select value={form.recurFreq} onChange={e=>upd("recurFreq",e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
                      {FREQ_OPTS.map(o=><option key={o.v} value={o.v}>{o.lb}</option>)}
                    </select>
                  </div>
                  {(form.recurFreq==="weekly"||form.recurFreq==="biweekly")&&(
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-2">On days</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {DAYS.map(d=>(
                          <button key={d} type="button" onClick={()=>togDay(d)} className={"text-xs px-2.5 py-1 rounded-full font-medium border transition-colors "+(form.recurDays.includes(d)?"border-transparent text-white":"border-slate-200 text-slate-500 hover:bg-slate-100")} style={form.recurDays.includes(d)?{background:BLUE}:{}}>
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.recurFreq==="monthly"&&(
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Day of month</label>
                      <select value={form.recurDay} onChange={e=>upd("recurDay",Number(e.target.value))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
                        {Array.from({length:31},(_,i)=>i+1).map(d=><option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  )}
                  {form.recurFreq==="custom"&&(
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Every N days</label>
                      <Inp type="number" min={1} max={365} value={form.recurInterval} onChange={e=>upd("recurInterval",Number(e.target.value))}/>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Visibility</label>
              <div className="grid grid-cols-3 gap-2">
                {[{v:"personal",lb:"Personal"},{v:"assigned",lb:"Assigned"},{v:"company",lb:"Company"}].map(({v,lb})=>(
                  <button key={v} onClick={()=>upd("vi",v)} className={"border rounded-xl p-3 text-left "+(form.vi===v?"border-transparent":"border-slate-200 hover:bg-slate-50")} style={form.vi===v?{background:BLUE}:{}}>
                    <div className={"mb-1 "+(form.vi===v?"text-white":"text-slate-500")}><VisIcon type={v} size={16}/></div><p className={"text-xs font-bold "+(form.vi===v?"text-white":"text-slate-700")}>{lb}</p>
                  </button>
                ))}
              </div>
            </div>
            {(form.vi==="assigned"||form.vi==="company")&&(
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Assign to{form.vi==="assigned"&&<span className="text-red-500"> *</span>}</label>
                {assignable.length===0
                  ?<p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {cu.role==="User"?"No manager assigned to your account — contact your Admin.":"No users available to assign."}
                  </p>
                  :<button type="button" onClick={()=>setShowAssignee(true)} className={"w-full flex items-center justify-between border rounded-xl px-3 py-2.5 bg-white hover:bg-slate-50 transition-colors "+(form.vi==="assigned"&&form.as===""?"border-red-300":"border-slate-200")}>
                    <AssigneeChip email={form.as} displayName={(allUsers.find(u=>u.email.toLowerCase()===form.as.toLowerCase()))?.displayName} placeholder={form.vi==="assigned"?"-- Select person --":"-- No specific assignee --"}/>
                    <ChevronDown size={14} className="text-slate-400 shrink-0 ml-2"/>
                  </button>
                }
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-500">Project{form.vi==="company"&&<span className="text-red-500"> *</span>}</label>
                {projLoading&&<span className="text-xs text-slate-400 flex items-center gap-1"><Spin/>Loading…</span>}
                {!projLoading&&projError&&<button onClick={fetchProjects} className="text-xs text-blue-500 hover:underline">Retry</button>}
              </div>
              {projError
                ?<p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{projError}</p>
                :<select value={form.proj} onChange={e=>upd("proj",e.target.value)} disabled={projLoading} className={"w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none "+(form.vi==="company"&&form.proj===""?"border-red-300":"border-slate-200")}>
                  <option value="">{projLoading?"Loading projects…":"-- No project --"}</option>
                  {customerProjects.length>0&&<optgroup label="Customer Projects">{customerProjects.map(p=><option key={p.spId} value={p.spId}>{projectDisplayName(p)}{p.isQuote?" [Quote]":""}</option>)}</optgroup>}
                  {internalProjects.length>0&&<optgroup label="Internal">{internalProjects.map(p=><option key={p.spId} value={p.spId}>{p.projectName}</option>)}</optgroup>}
                </select>}
              {!projLoading&&!projError&&form.vi==="company"&&form.proj===""&&<p className="text-xs text-red-500 mt-1">Project is required for company tasks.</p>}
            </div>
          </>}
          {step===2&&<>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Tags (comma separated)</label><Inp value={form.tg} onChange={e=>upd("tg",e.target.value)} placeholder="Robotics, QA..."/></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Notes</label><Txa value={form.no} onChange={e=>upd("no",e.target.value)} rows={3} placeholder="Blockers, links..."/></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-500 flex items-center gap-1"><GitBranch size={11}/>Dependencies</label>
                <button type="button" onClick={()=>setShowDeps(true)} className="text-xs px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 flex items-center gap-1">
                  <GitBranch size={11}/>Select tasks{form.dp.length>0&&<span className="ml-1 bg-blue-100 text-blue-700 font-bold rounded-full px-1.5 py-0.5 text-xs">{form.dp.length}</span>}
                </button>
              </div>
              {depTasks.length>0?(
                <div className="space-y-1">
                  {depTasks.map(t=>(
                    <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                      <span className="flex-1 text-sm truncate">{t.title}</span>
                      <StaBadge sta={t.sta}/>
                      <button type="button" onClick={()=>upd("dp",form.dp.filter(id=>id!==t.id))} className="text-slate-300 hover:text-red-400 ml-1"><X size={12}/></button>
                    </div>
                  ))}
                </div>
              ):<p className="text-xs text-slate-400 italic">No dependencies selected.</p>}
            </div>
          </>}
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 shrink-0">
          <div>{step>1&&<button onClick={()=>setStep(x=>x-1)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Back</button>}</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
            {step<steps.length
              ?<button onClick={()=>setStep(x=>x+1)} disabled={!valid&&step===1} className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-40" style={{background:BLUE}}>Next</button>
              :<button onClick={submit} disabled={!valid||saving} className="px-5 py-2 text-sm text-white font-semibold rounded-lg disabled:opacity-40 flex items-center gap-2" style={{background:BLUE}}>{saving&&<Spin/>}Create</button>}
          </div>
        </div>
      </div>
    </div>
    {showDeps&&<DepsPicker all={all} cu={cu} selected={form.dp} onChange={dp=>upd("dp",dp)} onClose={()=>setShowDeps(false)} projects={localProjects}/>}
    {showAssignee&&<AssigneePicker users={assignable} value={form.as} onChange={v=>upd("as",v)} onClose={()=>setShowAssignee(false)} required={form.vi==="assigned"} placeholder={form.vi==="assigned"?"-- Select person --":"-- No specific assignee --"}/>}
    </>
  );
}
